import {GameBoard, GameConfig, GameData, GamePlay, GamePlayKey, GameSetup} from "../types";
import {v4 as uuid} from "uuid";

// Waffle grid positions: 5x5 grid with gaps at [1,1],[1,3],[3,1],[3,3]
// Active cells (21 total):
// Row 0: (0,0)(0,1)(0,2)(0,3)(0,4)  - horizontal word 1
// Row 1: (1,0)     (1,2)     (1,4)  - vertical connectors
// Row 2: (2,0)(2,1)(2,2)(2,3)(2,4)  - horizontal word 2
// Row 3: (3,0)     (3,2)     (3,4)  - vertical connectors
// Row 4: (4,0)(4,1)(4,2)(4,3)(4,4)  - horizontal word 3

export type TilePosition = { row: number, col: number };

export const GRID_SIZE = 5;

export const GAP_POSITIONS: TilePosition[] = [
  {row: 1, col: 1}, {row: 1, col: 3},
  {row: 3, col: 1}, {row: 3, col: 3},
];

export function isGap(row: number, col: number): boolean {
  return GAP_POSITIONS.some(g => g.row === row && g.col === col);
}

export function getActiveCells(): TilePosition[] {
  const cells: TilePosition[] = [];
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (!isGap(row, col)) {
        cells.push({row, col});
      }
    }
  }
  return cells;
}

// The 6 words: 3 horizontal (rows 0,2,4) and 3 vertical (cols 0,2,4)
// Intersections occur at: (0,0),(0,2),(0,4),(2,0),(2,2),(2,4),(4,0),(4,2),(4,4)

export class SquareupLayout {

  constructor(
    readonly level: SquareupDifficultyLevel,
    readonly solutionWords: string[],  // 6 words: [h0, h1, h2, v0, v1, v2]
    readonly solutionGrid: string[][],  // 5x5 grid of solution letters (null for gaps)
    readonly scrambledGrid: string[][],  // 5x5 grid of scrambled letters (null for gaps)
    readonly allWords: string[],
  ) {
  }
}

export class SquareupDifficultyLevel {

  static readonly VALUES = new Array<SquareupDifficultyLevel>(3);
  static readonly EASY = SquareupDifficultyLevel.VALUES[0] = new SquareupDifficultyLevel("easy", "Easy", "15 swaps allowed", 15);
  static readonly MEDIUM = SquareupDifficultyLevel.VALUES[1] = new SquareupDifficultyLevel("medium", "Medium", "12 swaps allowed", 12);
  static readonly HARD = SquareupDifficultyLevel.VALUES[2] = new SquareupDifficultyLevel("hard", "Hard", "10 swaps allowed", 10);

  constructor(readonly name: string, readonly text: string, readonly description: string, readonly maxSwaps: number) {
  }
}

export class SquareupGameConfig extends GameConfig {

  constructor(readonly level: SquareupDifficultyLevel) {
    super();
  }
}

export class SquareupGameSetup extends GameSetup {

  constructor(key: GamePlayKey) {
    super(key);
  }
}

export class SquareupGameData extends GameData<SquareupGameBoard> {

  static async create(config: SquareupGameConfig): Promise<SquareupGameData> {
    const loader = new SquareupGamesLoader();
    const layout = await loader.getRandomGame(config.level);
    const setup = new SquareupGameSetup(new GamePlayKey("squareup", uuid()));
    const now = Date.now();
    return new SquareupGameData(new GamePlay(setup, now, 0, 0, 0, new SquareupGameBoard(config.level, layout)));
  }
}

export class SquareupGamesLoader {

  async getRandomGame(level: SquareupDifficultyLevel): Promise<SquareupLayout> {
    const wordsText = await fetch("/games/squareup/words.txt").then(result => result.text());
    const allWords = wordsText.split("\n").map(w => w.trim().toLowerCase()).filter(w => w.length === 5);

    // Build index: for each position (0-4) and letter, which words have that letter at that position
    const posIndex: Map<string, string[]>[] = [];
    for (let i = 0; i < 5; i++) {
      posIndex.push(new Map());
    }
    for (const word of allWords) {
      for (let i = 0; i < 5; i++) {
        const key = word[i];
        if (!posIndex[i].has(key)) {
          posIndex[i].set(key, []);
        }
        posIndex[i].get(key).push(word);
      }
    }

    // Try to find 6 compatible words
    let solutionWords: string[] | null = null;
    const maxAttempts = 500;

    for (let attempt = 0; attempt < maxAttempts && !solutionWords; attempt++) {
      solutionWords = this.tryGeneratePuzzle(allWords, posIndex);
    }

    if (!solutionWords) {
      // Fallback: use a known working set
      solutionWords = this.getFallbackPuzzle(allWords, posIndex);
    }

    // Build solution grid
    const solutionGrid = this.buildSolutionGrid(solutionWords);

    // Scramble the grid
    const scrambledGrid = this.scrambleGrid(solutionGrid, level);

    return new SquareupLayout(level, solutionWords, solutionGrid, scrambledGrid, allWords);
  }

  private tryGeneratePuzzle(allWords: string[], posIndex: Map<string, string[]>[]): string[] | null {
    // Pick first horizontal word randomly
    const h0 = allWords[Math.floor(Math.random() * allWords.length)];

    // Pick second horizontal word randomly
    const h1 = allWords[Math.floor(Math.random() * allWords.length)];
    if (h1 === h0) return null;

    // Pick third horizontal word randomly
    const h2 = allWords[Math.floor(Math.random() * allWords.length)];
    if (h2 === h0 || h2 === h1) return null;

    // Now find vertical words that match intersections
    // v0 goes down column 0: v0[0]=h0[0], v0[2]=h1[0], v0[4]=h2[0]
    const v0 = this.findVerticalWord(allWords, posIndex, h0[0], h1[0], h2[0]);
    if (!v0) return null;

    // v1 goes down column 2: v1[0]=h0[2], v1[2]=h1[2], v1[4]=h2[2]
    const v1 = this.findVerticalWord(allWords, posIndex, h0[2], h1[2], h2[2]);
    if (!v1 || v1 === v0) return null;

    // v2 goes down column 4: v2[0]=h0[4], v2[2]=h1[4], v2[4]=h2[4]
    const v2 = this.findVerticalWord(allWords, posIndex, h0[4], h1[4], h2[4]);
    if (!v2 || v2 === v0 || v2 === v1) return null;

    return [h0, h1, h2, v0, v1, v2];
  }

  private findVerticalWord(allWords: string[], posIndex: Map<string, string[]>[], char0: string, char2: string, char4: string): string | null {
    // Find words where position 0 = char0, position 2 = char2, position 4 = char4
    const candidates0 = posIndex[0].get(char0) || [];
    const candidates2Set = new Set(posIndex[2].get(char2) || []);
    const candidates4Set = new Set(posIndex[4].get(char4) || []);

    const matches = candidates0.filter(w => candidates2Set.has(w) && candidates4Set.has(w));
    if (matches.length === 0) return null;
    return matches[Math.floor(Math.random() * matches.length)];
  }

  private getFallbackPuzzle(allWords: string[], posIndex: Map<string, string[]>[]): string[] {
    // Brute force: try many horizontal combos until we find one that works
    for (let i = 0; i < 10000; i++) {
      const result = this.tryGeneratePuzzle(allWords, posIndex);
      if (result) return result;
    }
    // Ultimate fallback - should never reach here with 4625 words
    return ["steam", "raise", "drone", "sired", "earns", "melee"];
  }

  private buildSolutionGrid(words: string[]): string[][] {
    const [h0, h1, h2, v0, v1, v2] = words;
    const grid: string[][] = Array.from({length: 5}, () => Array(5).fill(null));

    // Horizontal words
    for (let c = 0; c < 5; c++) {
      grid[0][c] = h0[c];
      grid[2][c] = h1[c];
      grid[4][c] = h2[c];
    }

    // Vertical words (fill non-intersection cells)
    grid[1][0] = v0[1];
    grid[3][0] = v0[3];
    grid[1][2] = v1[1];
    grid[3][2] = v1[3];
    grid[1][4] = v2[1];
    grid[3][4] = v2[3];

    return grid;
  }

  private scrambleGrid(solutionGrid: string[][], level: SquareupDifficultyLevel): string[][] {
    const scrambled: string[][] = solutionGrid.map(row => [...row]);
    const activeCells = getActiveCells();

    // Determine which cells to keep green (intersection cells have higher chance)
    const intersectionCells = activeCells.filter(c => c.row % 2 === 0 && c.col % 2 === 0);
    const nonIntersectionCells = activeCells.filter(c => !(c.row % 2 === 0 && c.col % 2 === 0));

    // Keep some intersection cells green (about 2-3)
    const greenCells = new Set<string>();
    const numGreen = Math.floor(Math.random() * 2) + 2;  // 2-3 green cells
    const shuffledIntersections = [...intersectionCells].sort(() => Math.random() - 0.5);
    for (let i = 0; i < Math.min(numGreen, shuffledIntersections.length); i++) {
      greenCells.add(`${shuffledIntersections[i].row},${shuffledIntersections[i].col}`);
    }

    // Collect non-green cells for scrambling
    const swappableCells = activeCells.filter(c => !greenCells.has(`${c.row},${c.col}`));

    // Perform random swaps to scramble
    const numSwaps = Math.max(level.maxSwaps + 5, swappableCells.length);
    for (let i = 0; i < numSwaps; i++) {
      const idx1 = Math.floor(Math.random() * swappableCells.length);
      let idx2 = Math.floor(Math.random() * swappableCells.length);
      while (idx2 === idx1) {
        idx2 = Math.floor(Math.random() * swappableCells.length);
      }
      const c1 = swappableCells[idx1];
      const c2 = swappableCells[idx2];
      const temp = scrambled[c1.row][c1.col];
      scrambled[c1.row][c1.col] = scrambled[c2.row][c2.col];
      scrambled[c2.row][c2.col] = temp;
    }

    // Make sure the puzzle isn't already solved
    const isSolved = activeCells.every(c => scrambled[c.row][c.col] === solutionGrid[c.row][c.col]);
    if (isSolved) {
      // Do one more swap
      const c1 = swappableCells[0];
      const c2 = swappableCells[1];
      const temp = scrambled[c1.row][c1.col];
      scrambled[c1.row][c1.col] = scrambled[c2.row][c2.col];
      scrambled[c2.row][c2.col] = temp;
    }

    return scrambled;
  }
}

export class SquareupGameBoard extends GameBoard {

  swapsUsed: number = 0;

  constructor(readonly level: SquareupDifficultyLevel, readonly layout: SquareupLayout) {
    super();
  }

  getPoints(duration: number): number {
    const swapsRemaining = this.level.maxSwaps - this.swapsUsed;
    const basePoints = 100;
    const swapBonus = swapsRemaining * 50;
    const timeBonus = Math.max(0, 300 - Math.floor(duration / 1000));
    return basePoints + swapBonus + timeBonus;
  }

  toJSON(): any {
    return this;
  }
}
